import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tooltip,
  Card,
  CardMedia,
  CardContent
} from '@material-ui/core'
import { Rating } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Photo as PhotoIcon,
  GetApp as DownloadIcon,
  Print as PrintIcon,
  Flag as FlagIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  AccessTime as AccessTimeIcon
} from '@material-ui/icons'
import { format, parseISO } from 'date-fns'

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(3)
  },
  section: {
    marginBottom: theme.spacing(3)
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.primary.main
  },
  ratingBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  crewRatingCard: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2)
  },
  emojiRating: {
    fontSize: '2.5rem'
  },
  lqaTable: {
    marginTop: theme.spacing(2)
  },
  lqaCategory: {
    backgroundColor: theme.palette.grey[100],
    fontWeight: 600
  },
  issueCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.error.light + '20',
    border: `1px solid ${theme.palette.error.light}`
  },
  actionItemCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.warning.light + '20',
    border: `1px solid ${theme.palette.warning.light}`
  },
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2)
  },
  photoCard: {
    cursor: 'pointer',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'scale(1.05)'
    }
  },
  metaInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  priorityHigh: {
    color: theme.palette.error.main
  },
  priorityMedium: {
    color: theme.palette.warning.main
  },
  priorityLow: {
    color: theme.palette.info.main
  }
}))

const ReviewDetailModal = ({ open, onClose, review, onUpdate, onImageClick }) => {
  const classes = useStyles()
  
  if (!review) return null

  const getEmojiForRating = (rating) => {
    const emojiStyle = { fontSize: '2.5rem' }
    switch (rating) {
      case 3: return <span style={{ ...emojiStyle, color: '#4caf50' }}>😊</span>
      case 2: return <span style={{ ...emojiStyle, color: '#2196f3' }}>🙂</span>
      case 1: return <span style={{ ...emojiStyle, color: '#ff9800' }}>😐</span>
      case 0: return <span style={{ ...emojiStyle, color: '#f44336' }}>☹️</span>
      default: return <span style={{ ...emojiStyle, color: '#9e9e9e' }}>-</span>
    }
  }

  const getLQAStats = () => {
    if (!review.lqaResponses) return { yes: 0, no: 0, na: 0, total: 0 }
    
    let stats = { yes: 0, no: 0, na: 0, total: 0 }
    
    Object.values(review.lqaResponses).forEach(category => {
      Object.values(category).forEach(response => {
        stats.total++
        stats[response]++
      })
    })
    
    return stats
  }

  const lqaStats = getLQAStats()
  const complianceScore = lqaStats.total > 0 
    ? Math.round((lqaStats.yes / (lqaStats.yes + lqaStats.no)) * 100) 
    : 0

  const handlePrint = () => {
    window.print()
  }

  const handleExport = () => {
    const data = JSON.stringify(review, null, 2)
    const blob = new Blob([data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `review_${review.reviewId || review._id}_${Date.now()}.json`
    a.click()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">Review Details</Typography>
          <Box>
            <IconButton onClick={handlePrint}>
              <PrintIcon />
            </IconButton>
            <IconButton onClick={handleExport}>
              <DownloadIcon />
            </IconButton>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.metaInfo}>
          <Box className={classes.metaItem}>
            <LocationIcon color="action" />
            <Typography variant="body2">
              {review.site?.name || review.siteName}
            </Typography>
          </Box>
          <Box className={classes.metaItem}>
            <PersonIcon color="action" />
            <Typography variant="body2">
              {review.reviewedByName}
            </Typography>
          </Box>
          <Box className={classes.metaItem}>
            <AccessTimeIcon color="action" />
            <Typography variant="body2">
              {review.submittedAt && format(parseISO(review.submittedAt), 'MMM dd, yyyy h:mm a')}
            </Typography>
          </Box>
          <Chip
            label={review.crew?.onSite ? 'Site + Crew' : 'Site Only'}
            color={review.crew?.onSite ? 'primary' : 'default'}
            size="small"
          />
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Site Ratings
          </Typography>
          {review.ratings && (
            <Box className={classes.ratingBox}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="subtitle1">Overall:</Typography>
                    <Rating value={review.ratings.overall} readOnly size="large" />
                    <Typography variant="h6">{review.ratings.overall}/5</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2">Mowing</Typography>
                  <Rating value={review.ratings.mowing} readOnly size="small" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2">Trimming</Typography>
                  <Rating value={review.ratings.trimming} readOnly size="small" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2">Blowing</Typography>
                  <Rating value={review.ratings.blowing} readOnly size="small" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2">Edging</Typography>
                  <Rating value={review.ratings.edging} readOnly size="small" />
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>

        {review.crew?.ratings && (
          <Box className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Crew Performance
            </Typography>
            <Box className={classes.crewRatingCard}>
              <Typography variant="subtitle1" gutterBottom>
                <GroupIcon /> {review.crew.name}
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="body2" gutterBottom>Receptiveness</Typography>
                    {getEmojiForRating(review.crew.ratings.receptiveness)}
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="body2" gutterBottom>On Task</Typography>
                    {getEmojiForRating(review.crew.ratings.onTask)}
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="body2" gutterBottom>Attitude</Typography>
                    {getEmojiForRating(review.crew.ratings.attitude)}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}

        {review.lqaResponses && Object.keys(review.lqaResponses).length > 0 && (
          <Box className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              LQA Compliance
            </Typography>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <LinearProgress
                variant="determinate"
                value={complianceScore}
                style={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                color={complianceScore >= 80 ? 'primary' : 'secondary'}
              />
              <Typography variant="h6">{complianceScore}%</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`Yes: ${lqaStats.yes}`}
                  color="primary"
                />
              </Grid>
              <Grid item xs={4}>
                <Chip
                  icon={<CancelIcon />}
                  label={`No: ${lqaStats.no}`}
                  color="secondary"
                />
              </Grid>
              <Grid item xs={4}>
                <Chip
                  label={`N/A: ${lqaStats.na}`}
                />
              </Grid>
            </Grid>
            
            <TableContainer component={Paper} className={classes.lqaTable}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell>Question</TableCell>
                    <TableCell>Response</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(review.lqaResponses).map(([category, responses]) => (
                    Object.entries(responses).map(([question, answer], idx) => (
                      <TableRow key={`${category}_${question}`}>
                        {idx === 0 && (
                          <TableCell 
                            rowSpan={Object.keys(responses).length}
                            className={classes.lqaCategory}
                          >
                            {category.replace(/_/g, ' ')}
                          </TableCell>
                        )}
                        <TableCell>
                          {question.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={answer.toUpperCase()}
                            color={answer === 'yes' ? 'primary' : answer === 'no' ? 'secondary' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {review.issues && review.issues.length > 0 && (
          <Box className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Issues Reported ({review.issues.length})
            </Typography>
            {review.issues.map((issue, idx) => (
              <Paper key={idx} className={classes.issueCard}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      <WarningIcon color="error" /> {issue.title || 'Issue'}
                    </Typography>
                    <Typography variant="body2">{issue.text}</Typography>
                    <Box mt={1}>
                      <Chip
                        size="small"
                        label={issue.category}
                        variant="outlined"
                      />
                      <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                        {issue.timestamp && format(parseISO(issue.timestamp), 'MMM dd, h:mm a')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}

        {review.actionItems && review.actionItems.length > 0 && (
          <Box className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Action Items ({review.actionItems.length})
            </Typography>
            {review.actionItems.map((item, idx) => (
              <Paper key={idx} className={classes.actionItemCard}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="subtitle1" gutterBottom>
                      <FlagIcon 
                        className={
                          item.priority === 'high' ? classes.priorityHigh :
                          item.priority === 'medium' ? classes.priorityMedium :
                          classes.priorityLow
                        }
                      />
                      {' '}{item.text}
                    </Typography>
                    {item.description && (
                      <Typography variant="body2" color="textSecondary">
                        {item.description}
                      </Typography>
                    )}
                    <Box mt={1} display="flex" gap={1}>
                      <Chip
                        size="small"
                        label={item.priority}
                        color={
                          item.priority === 'high' ? 'secondary' :
                          item.priority === 'medium' ? 'default' : 'primary'
                        }
                      />
                      {item.assignedTo && (
                        <Chip
                          size="small"
                          label={`Assigned: ${item.assignedTo}`}
                          variant="outlined"
                        />
                      )}
                      {item.dueDate && (
                        <Chip
                          size="small"
                          label={`Due: ${format(parseISO(item.dueDate), 'MMM dd')}`}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}

        {review.photos && review.photos.length > 0 && (
          <Box className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              <PhotoIcon /> Photos ({review.photos.length})
            </Typography>
            <Box className={classes.photoGrid}>
              {review.photos.map((photo, idx) => (
                <Card 
                  key={idx} 
                  className={classes.photoCard}
                  onClick={() => onImageClick && onImageClick(review.photos, idx)}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={photo.uri || photo.url}
                    alt={`Review photo ${idx + 1}`}
                  />
                  <CardContent>
                    <Typography variant="caption" color="textSecondary">
                      Photo {idx + 1} of {review.photos.length}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {onUpdate && (
          <Button onClick={() => onUpdate(review)} variant="contained" color="primary">
            Update Review
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ReviewDetailModal